import React from 'react';

import { Card, Empty } from 'antd-v5';
import { FormattedMessage } from 'react-intl';

import type { YouthProgramAdvancement } from '@appTypes/esb';
import { ranksIds } from '@shared/constants';

import RankRow from './RankRow';
import styles from './YouthRanksCard.module.scss';
import { currentRankLevel } from './status';

export interface YouthRanksCardProps {
  /**
   * One program's ranks, straight from `GET /advancements/v2/youth/:userId/ranks`.
   * This card does no fetching: the caller picks which program to show.
   */
  program?: YouthProgramAdvancement;
  loading?: boolean;
  /**
   * Renders a "View More" button on each rank. Leave it out and no button shows,
   * which is what a read-only view wants.
   */
  onViewMore?: (rankId: number) => void;
  /**
   * Drops the Cub Scout Bobcat rank until the youth has earned it (SBL-5043).
   * Defaults to `true`, matching the youth profile page. Pass `false` for a view
   * that should list every rank the API returns.
   */
  hideUnearnedBobcat?: boolean;
}

export default function YouthRanksCard(props: YouthRanksCardProps) {
  const { program, loading, onViewMore, hideUnearnedBobcat = true } = props;

  const ranks = [...(program?.ranks ?? [])]
    .filter(
      rank =>
        !hideUnearnedBobcat || rank.id !== ranksIds.BOBCAT || !!rank.dateEarned,
    )
    .sort((a, b) => Number(a.level) - Number(b.level));

  const currentLevel = currentRankLevel(ranks);

  return (
    <Card className={styles.card} loading={loading}>
      {ranks.length ? (
        ranks.map(rank => (
          <RankRow
            key={rank.id}
            rank={rank}
            isCurrent={Number(rank.level) === currentLevel}
            onViewMore={onViewMore}
          />
        ))
      ) : (
        <Empty description={<FormattedMessage id="youthRanksCard.empty" />} />
      )}
    </Card>
  );
}
